/**
 * RC7 · M6 · Document Asset Store — Descriptor-only, biometric-gated C3 coordinator.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │   Canonical mobile catalogue: /api/mobile/documents/catalog │
 *   └───────────────────────────────┬─────────────────────────────┘
 *                                   ▼
 *                     ┌───────────────────────────┐
 *                     │   DocumentAssetStore      │
 *                     │  ┌──────┐  ┌──────┐       │
 *                     │  │ C1/  │  │ C3   │       │
 *                     │  │ C2   │  │reveal│       │
 *                     │  │descr.│  │gate  │       │
 *                     │  └──────┘  └──────┘       │
 *                     └────────────┬──────────────┘
 *                                  ▼
 *                       MobileDocumentsScreen
 *
 * NEVER a second document collection.
 * NEVER a second biometric implementation — delegates to c3_biometric.js.
 * NEVER caches document bytes offline in M6 (PO decision A · descriptor-only).
 *
 * M6 responsibilities encoded here:
 *   1. Partition catalogue by (user_id, voyage_id) and refuse cross-scope.
 *   2. Coordinate C3 reveal — biometric authentication → online byte
 *      fetch → transient in-memory hand-off → clear.
 *   3. Manage a SINGLE-SHOT reveal ticket per (voyage_id, document_id).
 *      There is NO persistent `biometric_unlocked=true` flag.
 *   4. Publish a subscribe() bus so React components clear their local
 *      reveal state on: 30-second timeout · route change · voyage
 *      switch · logout · app backgrounding.
 *   5. Refuse any offline C3 reveal — the offline path renders an
 *      "available online only" state (PO decision A).
 */
import { api } from "@/lib/api";
import {
  requestSensitiveIdentityAccess,
  inspectBiometric,
} from "@/mobile/offline/c3_biometric";

// M6 is descriptor-only — we do not consume the M4 Offline Voyage Pack.
// A minimal `isOnline` mirror keeps documentAssetStore.js self-contained
// and free of the pack.js dependency chain.
function isOnline() {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
}

// ---------------------------------------------------------------------------
// Reveal-state bus (single-shot, ephemeral). Subscribers receive
// `{ kind, scope }` events and MUST drop any sensitive state they hold.
// ---------------------------------------------------------------------------
const _subscribers = new Set();

function _emit(event) {
  for (const fn of Array.from(_subscribers)) {
    try { fn(event); } catch { /* subscriber isolated */ }
  }
}

/** Subscribe to reveal-clear events. Returns an unsubscribe fn. */
export function subscribeRevealClear(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

// ---------------------------------------------------------------------------
// Catalogue partitioning — never mixes users or voyages.
// ---------------------------------------------------------------------------
//
// We keep the most-recently fetched catalogue in memory ONLY. This is
// safe (descriptors are non-sensitive by construction), but we still
// namespace by (userId, voyageId) so a voyage switch never renders the
// prior partition. On any partition change we emit a clear-event so
// subscribers evict UI state.
let _catalogue = null;  // { userId, voyageId, generated_at, counts, descriptors }
// Active reveal ticket — refreshed per Reveal press.
let _activeReveal = null;  // { userId, voyageId, documentId, expiresAt }

/**
 * Fetch the safe descriptor catalogue for `(userId, voyageId)`.
 *
 * Never falls back to a stale catalogue from another voyage. Callers
 * SHOULD await this on voyage switch — the reveal bus fires a
 * "voyage_switch" clear immediately, and the descriptors from the new
 * voyage arrive as soon as the network returns.
 */
export async function loadCatalogue({ userId, voyageId }) {
  if (!userId || !voyageId) {
    throw new Error("DocumentAssetStore.loadCatalogue: missing user/voyage");
  }
  const prev = _catalogue;
  if (prev && (prev.userId !== userId || prev.voyageId !== voyageId)) {
    // Partition change — drop everything sensitive first.
    clearActiveReveal("voyage_switch");
  }
  const r = await api.get(
    // fold_pipeline_duplicates: this client lists pipeline documents via
    // /api/documents/pipeline/scoped, so byte-identical legacy rows are omitted.
    `/api/mobile/documents/catalog?voyage_id=${encodeURIComponent(voyageId)}&fold_pipeline_duplicates=1`,
  );
  const data = r?.data || {};
  // Defence-in-depth: refuse a mismatched voyage payload.
  if (data.voyage_id && data.voyage_id !== voyageId) {
    throw new Error("DocumentAssetStore: catalogue voyage_id mismatch");
  }
  _catalogue = {
    userId,
    voyageId,
    generated_at: data.generated_at || null,
    counts: data.counts || { C1: 0, C2: 0, C3: 0, total: 0 },
    descriptors: Array.isArray(data.descriptors) ? data.descriptors : [],
  };
  return _catalogue;
}

/** Return the current in-memory catalogue for `(userId, voyageId)` — or null. */
export function readCatalogue({ userId, voyageId }) {
  if (!_catalogue) return null;
  if (_catalogue.userId !== userId || _catalogue.voyageId !== voyageId) {
    return null;
  }
  return _catalogue;
}

// ---------------------------------------------------------------------------
// C3 reveal — biometric gate → online byte fetch (never persisted).
// ---------------------------------------------------------------------------
//
// This is the ONE path that yields sensitive content. There is NO
// separate `get(...)` that could bypass the biometric event.
//
// Contract:
//   · Descriptor.classification === "C3" is required.
//   · Device must be online — M6 does NOT hold C3 bytes offline.
//   · Biometric probe → authenticate → transient in-memory bytes.
//   · Caller receives `{ ok, reason, contentType, bodyBase64|body,
//                        expiresAt }` and MUST clear when done.
//
// If any step fails: FAIL CLOSED. No sensitive content is returned.

const REVEAL_TIMEOUT_MS = 30_000;  // PO decision — 30-second courtesy.

function _now() { return Date.now(); }

function _isC3(descriptor) {
  return descriptor && descriptor.classification === "C3";
}

/**
 * Perform a fresh biometric authentication then fetch the sensitive
 * bytes from the canonical online endpoint. Returns a REVEAL result:
 *
 *   {
 *     ok:          true,
 *     reason:      null,
 *     contentType: "application/pdf" | "application/json" | …,
 *     body:        <string|object>,          // canonical parsed payload
 *     bodyBase64:  <string>,                 // for pdf/image only
 *     expiresAt:   <epoch ms>,
 *     documentId:  <string>,
 *     voyageId:    <string>,
 *   }
 *
 * Or on failure:
 *   { ok:false, reason:"biometric_unavailable"|"biometric_cancelled"|
 *                       "biometric_failed"|"offline"|"c3_forbidden"|
 *                       "not_found"|"c3_class_missing"|"server_error",
 *     data:null }
 *
 * NEVER returns bytes on any failure path.
 */
export async function revealSensitiveDocument({
  userId, voyageId, descriptor, reason,
}) {
  if (!userId || !voyageId || !descriptor) {
    return { ok: false, reason: "missing_scope", data: null };
  }
  if (!_isC3(descriptor)) {
    return { ok: false, reason: "c3_class_missing", data: null };
  }
  if (!isOnline()) {
    // M6 is descriptor-only. Offline C3 reveal is INTENTIONALLY refused.
    return { ok: false, reason: "offline", data: null };
  }

  // 1. Fresh biometric event via the canonical gate.
  //    NO per-surface reimplementation.
  const auth = await requestSensitiveIdentityAccess(
    {
      user_id: userId,
      voyage_id: voyageId,
      sub_key: descriptor.document_id,
    },
    {
      reason: reason || `Reveal ${descriptor.display_name || "sensitive document"}`,
    },
  );
  if (!auth.ok) {
    return { ok: false, reason: auth.reason || "biometric_failed", data: null };
  }

  // 2. Discover the canonical fetch endpoint.
  let ctx;
  try {
    const r = await api.get(
      "/api/mobile/documents/reveal-context",
      {
        params: {
          voyage_id: voyageId,
          document_id: descriptor.document_id,
        },
      },
    );
    ctx = r?.data || null;
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) return { ok: false, reason: "not_found", data: null };
    if (status === 403) return { ok: false, reason: "c3_forbidden", data: null };
    return { ok: false, reason: "server_error", data: null };
  }
  if (!ctx || ctx.classification !== "C3" || !ctx.fetch_endpoint) {
    return { ok: false, reason: "c3_class_missing", data: null };
  }

  // 3. Online byte fetch. Bytes never persist beyond the caller's
  //    render surface; the caller is responsible for dropping the
  //    reference on timeout / navigation.
  let body;
  let contentType;
  let bodyBase64;
  try {
    // `cruise_documents` downloads are streamed as octet-stream.
    // `traveler_documents` are JSON records (no raw bytes today).
    if (descriptor.source === "cruise_documents") {
      const r = await api.get(ctx.fetch_endpoint, { responseType: "blob" });
      const blob = r?.data;
      contentType = r?.headers?.["content-type"] || "application/octet-stream";
      bodyBase64 = await _blobToBase64(blob);
      body = null;
    } else {
      const r = await api.get(ctx.fetch_endpoint);
      body = r?.data || null;
      contentType = "application/json";
      bodyBase64 = null;
      // Extract the specific traveler_documents entry the descriptor points at.
      if (body && Array.isArray(body.documents)) {
        const [, , docType] = String(descriptor.document_id).split("::");
        const found = body.documents.find((d) => d.type === docType);
        body = found || null;
      }
    }
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) return { ok: false, reason: "not_found", data: null };
    if (status === 401 || status === 403) {
      return { ok: false, reason: "c3_forbidden", data: null };
    }
    return { ok: false, reason: "server_error", data: null };
  }

  const expiresAt = _now() + REVEAL_TIMEOUT_MS;
  _activeReveal = {
    userId,
    voyageId,
    documentId: descriptor.document_id,
    expiresAt,
  };
  return {
    ok: true,
    reason: null,
    contentType,
    body,
    bodyBase64,
    expiresAt,
    documentId: descriptor.document_id,
    voyageId,
  };
}

// ---------------------------------------------------------------------------
// Reveal lifecycle · clearing hooks
// ---------------------------------------------------------------------------

/**
 * Drop the active reveal ticket and notify subscribers. Consumers use
 * `subscribeRevealClear` to receive this and evict local sensitive
 * state (e.g. rendered PDF blob, JSON payload).
 */
export function clearActiveReveal(kind) {
  const prev = _activeReveal;
  _activeReveal = null;
  _emit({ kind: kind || "manual", scope: prev || null });
}

/** For UI to know whether a courtesy timer should still run. */
export function activeRevealSnapshot() {
  return _activeReveal ? { ...(_activeReveal) } : null;
}

/**
 * Wire the browser-side clear triggers. Idempotent. Safe on non-DOM
 * environments (early-returns).
 */
let _wired = false;
export function installRevealClearHooks() {
  if (_wired || typeof window === "undefined") return;
  _wired = true;

  // Route change (hash router).
  window.addEventListener("hashchange", () => {
    clearActiveReveal("hashchange");
  });

  // App backgrounding (best-effort).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      clearActiveReveal("visibility_hidden");
    }
  });

  // Explicit logout signal — fired by `AuthProvider` on session end.
  window.addEventListener("cc:logout", () => {
    _catalogue = null;
    clearActiveReveal("logout");
  });

  // Explicit voyage-switch signal — fired by voyage picker.
  window.addEventListener("cc:voyage_switch", () => {
    _catalogue = null;
    clearActiveReveal("voyage_switch");
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function _blobToBase64(blob) {
  if (!blob) return null;
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const raw = String(reader.result || "");
      const comma = raw.indexOf(",");
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.readAsDataURL(blob);
  });
}

// Constants exposed for tests.
export const DOCUMENT_ASSET_STORE_REVEAL_TIMEOUT_MS = REVEAL_TIMEOUT_MS;
export const DOCUMENT_CLASSIFICATION = Object.freeze({
  C1: "C1", C2: "C2", C3: "C3",
});
